/**
 * Project template assignment component for admin workspace detail page.
 */

import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  useAdminWorkspaceTemplates,
  useAdminAssignTemplates,
} from "../../hooks/useAdminWorkspaces";
import { getProjectTemplates } from "../../services/naraDoor";
import { AlertCircle, Check, Loader2, FileText, X, Plus } from "lucide-react";

interface ProjectTemplateAssignmentProps {
  workspaceId: string;
}

export function ProjectTemplateAssignment({ workspaceId }: ProjectTemplateAssignmentProps) {
  // null = no edit in progress: the selection follows what the server holds.
  const [draftIds, setDraftIds] = useState<Set<string> | null>(null);
  const [success, setSuccess] = useState(false);

  const { data: assignedData, isLoading: isLoadingAssigned } = useAdminWorkspaceTemplates(workspaceId);
  const assignMutation = useAdminAssignTemplates();

  // Same key as the project templates list, so a rename there shows here.
  const { data: allData, isLoading: isLoadingAll, error: loadQueryError } = useQuery({
    queryKey: ["projectTemplates"],
    queryFn: () => getProjectTemplates(),
  });
  const allTemplates = allData?.templates || [];
  const loadError = loadQueryError ? loadQueryError.message || "Failed to load templates" : null;

  const assignedTemplates = assignedData?.templates || [];
  const selectedIds = draftIds ?? new Set(assignedTemplates.map((t) => t.id));
  const isLoading = isLoadingAll || isLoadingAssigned;

  // Only show templates visible to this workspace:
  // - Global templates (always visible)
  // - Workspace-owned by this workspace
  // - Workspace-scoped templates assigned to this workspace via junction table
  const visibleTemplates = useMemo(() =>
    allTemplates.filter(t => {
      if (t.scope === "global") return true;
      if (t.workspace_id) return t.workspace_id === workspaceId;
      return t.workspace_ids?.includes(workspaceId) ?? false;
    }),
    [allTemplates, workspaceId]
  );

  const handleToggleTemplate = (templateId: string) => {
    const next = new Set(selectedIds);
    if (next.has(templateId)) {
      next.delete(templateId);
    } else {
      next.add(templateId);
    }
    setDraftIds(next);
  };

  const handleSave = async () => {
    setSuccess(false);
    try {
      await assignMutation.mutateAsync({
        workspaceId,
        templateIds: Array.from(selectedIds),
      });
      setDraftIds(null);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      // Error is handled by mutation
    }
  };

  const hasChanges = () => {
    const currentIds = new Set(assignedTemplates.map((t) => t.id));
    if (currentIds.size !== selectedIds.size) return true;
    for (const id of selectedIds) {
      if (!currentIds.has(id)) return true;
    }
    return false;
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-12 bg-muted rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
        <AlertCircle className="h-5 w-5 text-red-600 shrink-0" />
        <p className="text-sm text-red-800 dark:text-red-200">{loadError}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {assignMutation.error && (
        <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
          <AlertCircle className="h-5 w-5 text-red-600 shrink-0" />
          <p className="text-sm text-red-800 dark:text-red-200">{assignMutation.error.message}</p>
        </div>
      )}

      {success && (
        <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg">
          <Check className="h-5 w-5 text-green-600 shrink-0" />
          <p className="text-sm text-green-800 dark:text-green-200">Templates updated successfully</p>
        </div>
      )}

      <div>
        <h3 className="text-sm font-medium text-foreground mb-3">
          Available Project Templates
        </h3>
        <p className="text-xs text-muted-foreground mb-4">
          Select which project templates should be available in this workspace.
        </p>

        {visibleTemplates.length === 0 ? (
          <div className="text-center py-8 bg-muted rounded-lg border border-border">
            <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No templates available</p>
          </div>
        ) : (
          <div className="space-y-2">
            {visibleTemplates.map((template) => {
              const isSelected = selectedIds.has(template.id);
              return (
                <div
                  key={template.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    isSelected
                      ? "border-blue-500 bg-blue-50 dark:bg-blue-950"
                      : "border-border hover:bg-accent"
                  }`}
                  onClick={() => handleToggleTemplate(template.id)}
                >
                  <div
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${
                      isSelected
                        ? "border-blue-500 bg-blue-500"
                        : "border-border"
                    }`}
                  >
                    {isSelected && <Check className="h-3 w-3 text-white" />}
                  </div>
                  <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate">
                      {template.name}
                    </p>
                    {template.description && (
                      <p className="text-xs text-muted-foreground truncate">
                        {template.description}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="pt-4 border-t border-border flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {selectedIds.size} template{selectedIds.size !== 1 ? "s" : ""} selected
        </p>
        <button
          onClick={handleSave}
          disabled={!hasChanges() || assignMutation.isPending}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {assignMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          Save Changes
        </button>
      </div>
    </div>
  );
}
